import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  TextField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  ChangePlayerZipCodeMutation,
  ChangePlayerZipCodeMutationVariables,
  PlayerZipCodeFormQuery,
} from './__generated__/PlayerZipCodeForm';

const query = graphql`
  query SanityPlayerZipCodeForm {
    sanityPlayerZipCodeForm {
      title {
        ...SanityLocaleString
      }
      zipCode {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const playerQuery = gql`
  query PlayerZipCodeForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
      city
      countryCode
      street
      zipCode
    }
  }
`;

const playerMutation = gql`
  mutation ChangePlayerZipCode(
    $playerId: ID!
    $city: String!
    $countryCode: String!
    $street: String!
    $zipCode: String!
  ) {
    changeAddress(
      playerId: $playerId
      city: $city
      countryCode: $countryCode
      street: $street
      zipCode: $zipCode
    ) {
      id
      city
      countryCode
      street
      zipCode
    }
  }
`;

const PlayerZipCodeForm: FC<{ playerId: string }> = ({ playerId }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerZipCodeFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerZipCodeForm;

  const [{ data }] = useQuery<PlayerZipCodeFormQuery>({
    query: playerQuery,
    variables: { playerId },
  });

  const [updatePlayerState, updatePlayer] = useMutation<
    ChangePlayerZipCodeMutation,
    ChangePlayerZipCodeMutationVariables
  >(playerMutation);

  const defaultValues = {
    zipCode: data?.player?.zipCode,
  };

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (values: typeof defaultValues) => {
    if (!data?.player?.id || !values.zipCode) {
      return;
    }

    updatePlayer({
      playerId: data?.player.id,
      street: data.player.street ?? '',
      city: data.player.city ?? '',
      countryCode: data.player.countryCode ?? '',
      zipCode: values.zipCode,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form || !data) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <TextField
            title={t(form.zipCode)}
            name="zipCode"
            id="PlayerZipCodeForm__zipCode"
            className="col-span-full"
          />

          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={updatePlayerState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerZipCodeForm;
